<template>
  <div
    v-outer-click="hideMenu"
    class="t-flyout-user-menu"
    :class="{ 'is-menu-open': isContextSwitchOpen }"
  >
    <div class="t-flyout-user-menu--action" @click="toggleWidget">
      <slot name="header">
        <div class="t-flyout-user-menu--header">
          <scale-icon-user-file-user accessibility-title="user" />
          <div data-matomo-mask class="user-title is-overflow-ellipsis">
            {{ fullName }}
          </div>
          <scale-icon-navigation-collapse-up
            v-if="isContextSwitchOpen"
            class="t-flyout-user-menu--arrow"
            size="14"
            accessibility-title="collapse-up"
          />
          <scale-icon-navigation-collapse-down
            v-else
            class="t-flyout-user-menu--arrow"
            size="14"
            accessibility-title="collapse-down"
          />
        </div>
      </slot>
    </div>
    <scale-card v-show="isContextSwitchOpen" class="t-flyout-user-menu--list">
      <div class="t-flyout-user-menu--list--item t-flyout-user-menu--list--item-header">
        <div data-matomo-mask class="t-item--heading">
          {{ fullName }}
        </div>
        <div data-matomo-mask class="t-item--subheading">
          {{ orgName }}
        </div>
      </div>
      <div class="t-flyout-user-menu--body">
        <div
          class="t-flyout-user-menu--list--item"
          @click="_localData.isLogoutAlertModalEnable = true"
        >
          <scale-icon name="user-logout" class="menu--list--item-icon" size="16" />
          <span>{{ t("topMenu.logout") }}</span>
        </div>
      </div>

      <!-- <div class="t-flex t-flex-justify-between t-flyout-user-menu--footer">
        <TLanguageSwitcher />
        // to be enable in future
        <div class="user-theme--modes">
          <scale-icon
            name="light-mode"
            class="theme-option is-active"
            size="24"
          />
          <scale-icon
            name="dark-mode"
            class="theme-option"
            size="24"
          />
        </div>
      </div> -->
    </scale-card>
  </div>
  <LogoutAlertModal
    v-if="_localData.isLogoutAlertModalEnable"
    @exit="_localData.isLogoutAlertModalEnable = false"
    @logout="logout"
  />
</template>

<script lang="ts" setup>
import { ref, computed, watch, reactive } from "vue"
import { useRoute } from "vue-router"
import LogoutAlertModal from "./LogoutAlertModal.vue"
import { useI18n } from "vue-i18n"
import AuthService from "@/common/services/AuthServices"

const { t } = useI18n()

const route = useRoute()

const isContextSwitchOpen = ref<boolean>(false)

const _localData = reactive({
  isLogoutAlertModalEnable: false
})

const toggleWidget = (): void => {
  isContextSwitchOpen.value = !isContextSwitchOpen.value
}

const hideMenu = (): void => {
  isContextSwitchOpen.value = false
}

const fullName = computed((): string => {
  return AuthService.getLoggedUser().name ?? "NA"
})

const orgName = computed((): string => {
  return AuthService.getLoggedUser().company ?? "-"
})

watch(route, () => {
  hideMenu()
})

function logout(): void {
  AuthService.doLogout()
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins/typography.scss" as *;
@use "@/styles/variables/spacing.scss" as *;

.t-flyout-user-menu {
  --spacing-x: var(--scl-spacing-12);
  --spacing-y: var(--scl-spacing-12);

  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    color: var(--telekom-color-text-and-icon-standard);
  }

  &--header {
    @include tg-ui;

    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--telekom-color-text-and-icon-standard);

    .user-title {
      @include tg-small-bold;
      margin-left: var(--scl-spacing-8);
    }

    &:hover {
      color: var(--telekom-color-text-and-icon-primary-standard);
    }
  }

  &--action {
    padding-right: 20px;
  }

  &--arrow {
    position: absolute;
    right: 0;
    margin-left: var(--scl-spacing-4);
  }

  &--list {
    --spacing-body: 0; // scale-card: padding: 0

    cursor: auto;
    width: 256px;
    right: 0;
    position: absolute;
    top: 120%;
    z-index: 99;

    &--item-header {
      background: var(--telekom-color-ui-faint);
    }
  }

  &--list--item {
    display: flex;
    flex-direction: column;
    gap: $space-1;
    color: var(--telekom-color-text-and-icon-standard);
    font-weight: var(--telekom-typography-font-weight-medium);
    font-size: var(--scl-font-size-16);
    line-height: 100%;
    padding: var(--spacing-x);
    position: relative;
    cursor: auto;

    .t-item--subheading {
      @include tg-small-bold;
      color: var(--telekom-color-text-and-icon-additional);
    }

    &:not(.t-flyout-user-menu--list--item-header) {
      display: flex;
      align-items: flex-start;
      cursor: pointer;
      padding-left: $space-8;
    }

    &:not(.t-flyout-user-menu--list--item-header):hover {
      color: var(--telekom-color-text-and-icon-primary-standard);
    }

    span {
      display: inline-block;
      margin-left: $space-2;
    }

    .menu--list--item-icon {
      position: absolute;
      left: $space-3;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  &--footer {
    border-top: 1px solid var(--telekom-color-ui-faint);
    padding: $space-4 $space-3;
    align-items: center;
  }

  // TODO: Use this code when dark/light mode and language switch is enabled
  //
  // .user-theme--modes {
  //   .theme-option {
  //     color: var(--telekom-color-text-and-icon-standard);
  //
  //     &:hover,
  //     &.is-active {
  //       color: var(--telekom-color-text-and-icon-primary-standard);
  //       cursor: pointer;
  //     }
  //   }
  // }
}
</style>
